import { useCallback, useEffect, useMemo, useState } from 'react';
import { PageShell } from '../layout/page-shell.jsx';
import { useToast } from '../ui/toast.jsx';
import { formatUtcInTimezone } from '../../lib/time.js';
import { api, ApiError } from '../../api/index.js';
import {
  LastRefresh, ExportButtons, SearchInput, applySearch,
  useTableState, SortIcon, Pagination,
} from '../../lib/table-utils.jsx';

/**
 * NFFI Bridge — admin page. Faithful port of reference
 * `manuals/COP-microservice-integration/NFFI Bridge/nffi-page.jsx`.
 *
 * Adjustments vs reference (coord plumbing only):
 *   - PageShell outer chrome (icon + title) — common to every coord page.
 *   - useToast() instead of onToast prop.
 *   - formatUtcInTimezone(iso, tz) instead of formatInTz.
 *   - api.cop.nffi.* instead of api.getNatoNffi* / api.pollNatoNffi*.
 *   - copLayers fetched locally via api.cop.getLayers().
 *   - 503 -> notInstalled banner (ad-cop-service unreachable diagnostic).
 *   - Live Config (Hot config) section is always rendered (reference wraps it
 *     in `{hot && (...)}`; coord shows the widget skeleton even when hot is
 *     null so operators always see the 5 widgets — fields fall back to safe
 *     placeholders when hot is missing).
 *
 * The widget structure (sections) is identical to the reference.
 */

const POLL_MS = 5000;
const MSG_PAGE_SIZE = 25;

function statusBadgeClass(status) {
  if (status === 'up')    return 'bg-emerald-50 border border-emerald-200 text-emerald-700';
  if (status === 'down')  return 'bg-slate-50 border border-slate-200 text-slate-600';
  if (status === 'error') return 'bg-rose-50 border border-rose-200 text-rose-700';
  if (status === 'config_load_failed') return 'bg-rose-50 border border-rose-200 text-rose-700';
  return 'bg-slate-50 border border-slate-200 text-slate-500';
}

function statusIcon(status) {
  if (status === 'up')    return 'fa-circle-check';
  if (status === 'down')  return 'fa-circle-stop';
  if (status === 'error') return 'fa-circle-exclamation';
  if (status === 'config_load_failed') return 'fa-triangle-exclamation';
  return 'fa-circle-dot';
}

function bytesHuman(n) {
  if (!Number.isFinite(n) || n === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0; let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export function CopNffiBridgePage({ currentUser }) {
  const toast = useToast();
  const tz = currentUser?.timezone || 'UTC';
  const formatDate = useCallback((iso, opts) => iso ? formatUtcInTimezone(iso, tz, opts) : '—', [tz]);

  const [status, setStatus] = useState(null);
  const [copLayers, setCopLayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [polling, setPolling] = useState(false);
  const [messageDir, setMessageDir] = useState('rx');
  const [messages, setMessages] = useState([]);
  const [unmapped, setUnmapped] = useState([]);
  const [savingConfig, setSavingConfig] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [msgSearch, setMsgSearch] = useState('');
  const [pollingPeer, setPollingPeer] = useState(null);
  const [notInstalled, setNotInstalled] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const data = await api.cop.nffi.status();
      setStatus(data);
      setRefreshedAt(new Date());
      setNotInstalled(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        setNotInstalled(err.payload || { reason: 'unknown' });
      } else {
        toast.error(`NFFI status failed: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const loadCopLayers = useCallback(async () => {
    try {
      const res = await api.cop.getLayers();
      setCopLayers(res?.layers || []);
    } catch { /* ignore */ }
  }, []);

  async function handlePollPeer(peerId) {
    setPollingPeer(peerId);
    try {
      const result = await api.cop.nffi.pollProvider(peerId);
      if (result?.ok) {
        toast.success(`Polled ${peerId}`);
        setTimeout(loadStatus, 500);
      } else {
        toast.error(`Poll failed: ${result?.error || 'unknown'}`);
      }
    } catch (err) {
      toast.error(`Poll failed: ${err.message}`);
    } finally {
      setPollingPeer(null);
    }
  }

  const searchedMessages = useMemo(
    () => applySearch(messages, msgSearch, [
      'peerId', 'trackId', 'typeId', 'category', 'callsign', 'lookupVia',
      'operation', 'remote',
    ]),
    [messages, msgSearch]
  );
  const msgTable = useTableState(searchedMessages, {
    defaultSortKey: 'ts',
    defaultSortDir: 'desc',
    pageSize: MSG_PAGE_SIZE,
    sortAccessors: {
      ts:        (m) => m.ts || '',
      peerId:    (m) => m.peerId || '',
      trackId:   (m) => m.trackId || '',
      typeId:    (m) => m.typeId || '',
      category:  (m) => m.category || '',
      callsign:  (m) => m.callsign || '',
    },
  });

  const messageExportColumns = useMemo(() => [
    { key: 'ts',        label: 'Time (ISO)' },
    { key: 'peerId',    label: 'Peer' },
    { key: 'trackId',   label: 'Track ID' },
    { key: 'typeId',    label: 'Type ID' },
    { key: 'category', label: 'Category' },
    { key: 'lat',       label: 'Lat' },
    { key: 'lon',       label: 'Lon' },
    { key: 'callsign',  label: 'Callsign' },
    { key: 'lookupVia', label: 'Via' },
    { key: 'operation', label: 'Operation' },
    { key: 'remote',    label: 'Remote' },
    { key: 'tracks',    label: 'Tracks' },
    { key: 'bytes',     label: 'Response bytes' },
  ], []);

  const providerExportColumns = useMemo(() => [
    { key: 'id',                  label: 'Peer' },
    { key: 'direction',           label: 'Direction' },
    { key: 'status',              label: 'Status' },
    { key: 'pollCount',           label: 'Polls' },
    { key: 'errorCount',          label: 'Errors' },
    { key: 'lastPoll',            label: 'Last poll (ISO)' },
    { key: 'lastSuccessfulPoll',  label: 'Last successful (ISO)' },
    { key: 'currentBackoffMs',    label: 'Backoff (ms)' },
    { key: 'rxCount',             label: 'RX' },
    { key: 'txCount',             label: 'TX' },
    { key: 'bytesIn',             label: 'Bytes In' },
    { key: 'bytesOut',            label: 'Bytes Out' },
    { key: 'lastError',           label: 'Last error' },
  ], []);

  const loadMessages = useCallback(async (dir) => {
    try {
      const data = await api.cop.nffi.messages(dir);
      setMessages(data.messages || []);
    } catch { /* ignore */ }
  }, []);

  const loadUnmapped = useCallback(async () => {
    try {
      const data = await api.cop.nffi.unmapped();
      setUnmapped(data.items || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadStatus(); loadUnmapped(); loadCopLayers(); }, [loadStatus, loadUnmapped, loadCopLayers]);
  useEffect(() => { loadMessages(messageDir); }, [messageDir, loadMessages]);

  useEffect(() => {
    const t = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      loadStatus();
      loadMessages(messageDir);
    }, POLL_MS);
    return () => clearInterval(t);
  }, [loadStatus, loadMessages, messageDir]);

  async function handleRefresh() {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await Promise.all([loadStatus(), loadMessages(messageDir), loadUnmapped(), loadCopLayers()]);
    } finally {
      setRefreshing(false);
    }
  }

  async function handlePollNow() {
    if (polling) return;
    setPolling(true);
    try {
      const res = await api.cop.nffi.pollNow();
      toast.success(`Forced poll on ${res.peers || 0} provider(s)`);
      await loadStatus();
    } catch (err) {
      toast.error(`Poll failed: ${err.message}`);
    } finally {
      setPolling(false);
    }
  }

  async function saveConfigPatch(patch, label) {
    setSavingConfig(true);
    try {
      await api.cop.nffi.setRuntimeConfig(patch);
      toast.success(`${label} updated`);
      await loadStatus();
    } catch (err) {
      toast.error(`Update failed: ${err.message}`);
    } finally {
      setSavingConfig(false);
    }
  }

  const moduleStatus = status?.moduleStatus || (status?.enabled === false ? 'down' : 'down');
  const isConfigError = moduleStatus === 'config_load_failed';
  const hot = status?.hotConfig || null;
  const exportLayers = hot?.outbound?.exportLayers || [];

  return (
    <PageShell icon="fa-handshake-angle" title="NFFI Bridge">
      {notInstalled ? (
        <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <i className="fa-sharp fa-solid fa-triangle-exclamation mr-2" />
          COP ADMS service is not reachable
          {notInstalled.reason ? ` (${notInstalled.reason})` : ''}. Open
          <span className="font-medium"> COP ADMS → Datasets</span> for diagnostics.
        </div>
      ) : loading ? (
        <div className="rounded-[28px] border border-line bg-white p-10 text-center text-sm text-mist">
          Loading NFFI status...
        </div>
      ) : (
        <div className="space-y-5">
          {/* ── Header ── */}
          <section className="rounded-[28px] border border-line bg-white p-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-slate-700">
                  <i className="fa-sharp fa-solid fa-handshake-angle" />
                  NATO NFFI Bridge
                </div>
                <p className="text-sm text-mist">
                  NATO Friendly Force Information (STANAG 5527) — SOAP/HTTPS coalition position exchange. Version: {hot?.version || '1.3'}.
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                <LastRefresh ts={refreshedAt} />
                <button className="btn-secondary" onClick={handleRefresh} disabled={refreshing}>
                  <i className={`fa-sharp fa-solid fa-arrows-rotate ${refreshing ? 'animate-spin' : ''}`} />
                  {refreshing ? 'Refreshing...' : 'Refresh'}
                </button>
                <button className="btn-primary" onClick={handlePollNow} disabled={polling || (status?.peers || []).filter((p) => p.direction === 'inbound').length === 0}>
                  <i className={`fa-sharp fa-solid fa-cloud-arrow-down ${polling ? 'animate-spin' : ''}`} />
                  {polling ? 'Polling...' : 'Poll Now (all)'}
                </button>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${statusBadgeClass(moduleStatus)}`}>
                <i className={`fa-sharp fa-solid ${statusIcon(moduleStatus)}`} />
                {moduleStatus.replace(/_/g, ' ')}
              </span>
              <span className="text-xs text-mist">
                Mappings: <span className="font-mono">{status?.mappedCategoryCount ?? '—'}</span>
              </span>
              <span className="text-xs text-mist">
                Providers: <span className="font-mono">{(status?.peers || []).filter((p) => p.direction === 'inbound').length}</span>
              </span>
              <span className="text-xs text-mist">
                Outbound: <span className="font-mono">{hot?.outbound?.enabled ? 'enabled' : 'disabled'}</span>
              </span>
              <span className="text-xs text-mist">
                Polls: <span className="font-mono tabular-nums">{status?.counts?.pollTotal ?? 0}</span>
              </span>
              <span className="text-xs text-mist">
                Poll errors: <span className="font-mono tabular-nums">{status?.counts?.pollErrorsTotal ?? 0}</span>
              </span>
              <span className="text-xs text-mist">
                RX: <span className="font-mono tabular-nums">{status?.counts?.rxTotal ?? 0}</span>
              </span>
              <span className="text-xs text-mist">
                TX: <span className="font-mono tabular-nums">{status?.counts?.txTotal ?? 0}</span>
              </span>
              {status?.startedAt && (
                <span className="text-xs text-mist">
                  Started: <span className="font-mono">{formatDate(status.startedAt)}</span>
                </span>
              )}
            </div>

            {isConfigError && (
              <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
                <div className="font-semibold">Configuration error in data/nffi.json</div>
                <div className="mt-1 font-mono text-xs">{status?.configError || '(no detail)'}</div>
              </div>
            )}
          </section>

          {/* ── Providers + outbound ── */}
          <section className="rounded-[28px] border border-line bg-white p-5">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h3 className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-mist">
                <i className="fa-sharp fa-solid fa-plug text-slate-400" />
                Providers &amp; Endpoint
              </h3>
              <ExportButtons
                getRows={() => [...(status?.peers || [])].sort((a, b) => a.id.localeCompare(b.id))}
                columns={providerExportColumns}
                filenameBase="nffi-providers"
                disabled={(status?.peers || []).length === 0}
                small
              />
            </div>
            {(status?.peers || []).length === 0 ? (
              <div className="py-6 text-center text-sm text-mist">
                No NFFI providers configured. Add to <code className="rounded bg-slate-100 px-1 font-mono text-xs">data/nffi.json</code> and restart.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-line text-xs font-semibold uppercase tracking-[0.12em] text-mist">
                      <th className="pb-3 pr-4">Peer</th>
                      <th className="pb-3 pr-4">Direction</th>
                      <th className="pb-3 pr-4">Status</th>
                      <th className="pb-3 pr-4 text-right">Polls</th>
                      <th className="pb-3 pr-4 text-right">Errors</th>
                      <th className="pb-3 pr-4">Last poll</th>
                      <th className="pb-3 pr-4">Last success</th>
                      <th className="pb-3 pr-4">Backoff</th>
                      <th className="pb-3 pr-4 text-right">RX</th>
                      <th className="pb-3 pr-4 text-right">TX</th>
                      <th className="pb-3 pr-4 text-right">Bytes In</th>
                      <th className="pb-3 pr-4 text-right">Bytes Out</th>
                      <th className="pb-3 pr-4">Last error</th>
                      <th className="pb-3 pr-4 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...status.peers].sort((a, b) => a.id.localeCompare(b.id)).map((p) => {
                      const failurePct = p.pollCount > 0 ? Math.round((p.pollErrors / p.pollCount) * 100) : 0;
                      const isPollingThis = pollingPeer === p.id;
                      return (
                        <tr key={p.id} className="border-b border-line last:border-0 hover:bg-slate-50">
                          <td className="py-3 pr-4 font-mono text-xs text-ink">{p.id}</td>
                          <td className="py-3 pr-4">
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                              p.direction === 'inbound' ? 'bg-blue-50 text-blue-700' : 'bg-emerald-50 text-emerald-700'
                            }`}>
                              {p.direction}
                            </span>
                          </td>
                          <td className="py-3 pr-4">
                            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusBadgeClass(p.status)}`}>
                              <i className={`fa-sharp fa-solid ${statusIcon(p.status)} text-[8px]`} />
                              {p.status}
                            </span>
                          </td>
                          <td className="py-3 pr-4 text-right tabular-nums font-mono text-xs text-mist">{p.pollCount}</td>
                          <td className="py-3 pr-4 text-right tabular-nums font-mono text-xs">
                            <span className={failurePct > 50 ? 'text-rose-600' : failurePct > 10 ? 'text-amber-600' : 'text-mist'}>
                              {p.pollErrors} ({failurePct}%)
                            </span>
                          </td>
                          <td className="py-3 pr-4 text-xs text-mist tabular-nums">{p.lastPoll ? formatDate(p.lastPoll, { second: '2-digit' }) : '—'}</td>
                          <td className="py-3 pr-4 text-xs text-mist tabular-nums">{p.lastSuccessfulPoll ? formatDate(p.lastSuccessfulPoll, { second: '2-digit' }) : '—'}</td>
                          <td className="py-3 pr-4 text-xs">
                            {p.currentIntervalMs ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                                <i className="fa-sharp fa-solid fa-clock-rotate-left text-[8px]" />
                                {Math.round(p.currentIntervalMs / 1000)}s
                              </span>
                            ) : (
                              <span className="text-mist text-[10px]">baseline</span>
                            )}
                          </td>
                          <td className="py-3 pr-4 text-right tabular-nums font-mono text-xs text-mist">{p.rxCount}</td>
                          <td className="py-3 pr-4 text-right tabular-nums font-mono text-xs text-mist">{p.txCount}</td>
                          <td className="py-3 pr-4 text-right tabular-nums font-mono text-xs text-mist">{bytesHuman(p.bytesIn)}</td>
                          <td className="py-3 pr-4 text-right tabular-nums font-mono text-xs text-mist">{bytesHuman(p.bytesOut)}</td>
                          <td className="py-3 pr-4 text-xs text-rose-600 font-mono max-w-[180px] truncate" title={p.lastError || ''}>{p.lastError || '—'}</td>
                          <td className="py-3 pr-4 text-center">
                            {p.direction === 'inbound' ? (
                              <button
                                type="button"
                                className="rounded-lg border border-line bg-white px-2 py-1 text-[11px] font-medium text-mist hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                onClick={() => handlePollPeer(p.id)}
                                disabled={isPollingThis}
                                title="Trigger an immediate poll on this provider (granular alternative to the global Poll Now)"
                              >
                                <i className={`fa-sharp fa-solid fa-cloud-arrow-down mr-1 ${isPollingThis ? 'animate-spin' : ''}`} />
                                {isPollingThis ? '...' : 'Poll'}
                              </button>
                            ) : (
                              <span className="text-[10px] text-mist">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* ── Imported NFFI Layers ── */}
          {(() => {
            const nffiLayers = (copLayers || [])
              .filter((l) => (l.datasetId || '').startsWith('cop-ds-nffi-'))
              .sort((a, b) => a.name.localeCompare(b.name));
            const totalObjects = nffiLayers.reduce((s, l) => s + (l.objectCount || 0), 0);
            return (
              <section className="rounded-[28px] border border-line bg-white p-5">
                <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
                  <h3 className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-mist">
                    <i className="fa-sharp fa-solid fa-handshake-angle text-violet-400" />
                    Imported NFFI Layers
                  </h3>
                  <div className="text-xs text-mist">
                    <span className="font-mono tabular-nums">{nffiLayers.length}</span> layer{nffiLayers.length === 1 ? '' : 's'}
                    {' · '}
                    <span className="font-mono tabular-nums">{totalObjects}</span> object{totalObjects === 1 ? '' : 's'}
                  </div>
                </div>
                <p className="mb-3 text-xs text-mist">
                  Layers received from configured NFFI providers and stored under <code className="rounded bg-slate-100 px-1 font-mono text-[10px]">cop-ds-nffi-*</code> datasets. Each represents a peer&apos;s last reported track set; objects refresh on every successful poll.
                </p>
                <div className="rounded-xl border border-line bg-slate-50/40">
                  <div className="flex items-center justify-between gap-2 rounded-t-xl border-b border-line bg-violet-50 px-3 py-2 text-violet-700">
                    <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
                      <i className="fa-sharp fa-solid fa-handshake-angle" />
                      NFFI Datasets
                    </div>
                    <span className="rounded-full bg-white/70 px-2 py-0.5 font-mono text-[10px] tabular-nums">
                      {nffiLayers.length}
                    </span>
                  </div>
                  <div className="max-h-[1200px] overflow-y-auto p-2 space-y-0.5">
                    {nffiLayers.length === 0 ? (
                      <div className="px-2 py-4 text-center text-[11px] text-mist">
                        No NFFI layers yet. They appear once a configured provider returns track data on a successful poll.
                      </div>
                    ) : nffiLayers.map((l) => (
                      <div key={l.id} className="flex items-center gap-2 rounded p-1 text-xs">
                        <i className="fa-sharp fa-solid fa-layer-group text-[10px] text-mist" />
                        <span className="min-w-0 flex-1 truncate font-mono text-ink" title={l.name}>{l.name}</span>
                        <span className="shrink-0 text-mist tabular-nums">({l.objectCount || 0})</span>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            );
          })()}

          {/* ── Hot config / Outbound ──
              Always rendered for parity with the CoT page (5-widget skeleton).
              Reference wraps in `{hot && (...)}`; fields here fall back to "—"
              when hot is null and controls disable. Toggling exposed layers
              requires hot to be present (module enabled in data/nffi.json). */}
          {(() => {
            const outboundEnabled = !!hot?.outbound?.enabled;
            const endpointStr = hot
              ? `${hot.outbound?.host || '—'}:${hot.outbound?.port || '—'}${hot.outbound?.path || ''}`
              : '—';
            const groups = { cop: [], nffi: [] };
            for (const layer of copLayers) {
              const dsId = layer.datasetId || '';
              if (dsId.startsWith('cop-ds-nffi-')) groups.nffi.push(layer);
              else if (dsId.startsWith('cop-ds-cot-')) continue;
              else groups.cop.push(layer);
            }
            for (const k of Object.keys(groups)) {
              groups[k].sort((a, b) => a.name.localeCompare(b.name));
            }

            const renderColumn = (title, icon, badgeClass, layersList, emptyText) => (
              <div className={`flex min-w-0 flex-col rounded-xl border border-line bg-slate-50/40 ${outboundEnabled ? '' : 'opacity-60'}`}>
                <div className={`flex items-center justify-between gap-2 rounded-t-xl border-b border-line px-3 py-2 ${badgeClass}`}>
                  <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
                    <i className={`fa-sharp fa-solid ${icon}`} />
                    {title}
                  </div>
                  <span className="rounded-full bg-white/70 px-2 py-0.5 font-mono text-[10px] tabular-nums">
                    {layersList.length}
                  </span>
                </div>
                <div className="max-h-[1200px] overflow-y-auto p-2 space-y-0.5">
                  {layersList.length === 0 ? (
                    <div className="px-2 py-4 text-center text-[11px] text-mist">{emptyText}</div>
                  ) : layersList.map((l) => (
                    <label
                      key={l.id}
                      className={`flex items-center gap-2 rounded p-1 text-xs ${outboundEnabled ? 'cursor-pointer hover:bg-white' : 'cursor-not-allowed'}`}
                      title={outboundEnabled ? l.name : 'Outbound is disabled — enable in nffi.json'}
                    >
                      <input
                        type="checkbox"
                        checked={exportLayers.includes(l.id)}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...exportLayers, l.id]
                            : exportLayers.filter((x) => x !== l.id);
                          saveConfigPatch({ exportLayers: next }, 'Export layers');
                        }}
                        disabled={savingConfig || !outboundEnabled}
                      />
                      <span className="min-w-0 flex-1 truncate font-mono" title={l.name}>{l.name}</span>
                      <span className="shrink-0 text-mist tabular-nums">({l.objectCount || 0})</span>
                    </label>
                  ))}
                </div>
              </div>
            );

            return (
              <section className="rounded-[28px] border border-line bg-white p-5 space-y-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-mist">
                      <i className="fa-sharp fa-solid fa-bolt text-slate-400" />
                      Live Config {outboundEnabled ? '(no restart needed)' : ''}
                    </h3>
                    <p className="mt-1 text-xs text-mist">
                      Outbound exposes our NFFI provider on <code className="rounded bg-slate-100 px-1 font-mono text-[10px]">{endpointStr}</code>. Selected layers are returned to partners that POST a SOAP GetFFIDataRequest.
                    </p>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${outboundEnabled ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-slate-100 border border-slate-200 text-slate-600'}`}>
                    <i className={`fa-sharp fa-solid ${outboundEnabled ? 'fa-circle-check' : 'fa-circle-stop'}`} />
                    Outbound {outboundEnabled ? 'enabled' : 'disabled'}
                  </span>
                </div>

                {!outboundEnabled && (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50/60 px-4 py-2.5 text-xs text-amber-800">
                    <i className="fa-sharp fa-solid fa-circle-info mr-1.5" />
                    Outbound is disabled. Set <code className="rounded bg-white/70 px-1 font-mono text-[10px]">outbound.enabled: true</code> in <code className="rounded bg-white/70 px-1 font-mono text-[10px]">data/nffi.json</code> and restart the service to start serving the layers selected below.
                  </div>
                )}

                <div className="space-y-2">
                  <div className="inline-flex items-center gap-2 text-sm font-medium text-ink">
                    <i className="fa-sharp fa-solid fa-tower-broadcast text-slate-400" />
                    Outbound NFFI Provider — Exposed Layers
                    {outboundEnabled && (
                      <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                        <i className="fa-sharp fa-solid fa-floppy-disk text-[8px]" />
                        auto-saves
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-mist mb-2">
                    {outboundEnabled
                      ? 'Toggling a checkbox saves immediately. Selected layers are returned to partners on every NFFI request to our endpoint. NFFI-imported can be re-broadcast to OTHER NFFI peers (echo-prevention at runtime via _natoSource tagging). CoT-imported is excluded — cross-protocol bridging is out of scope of STANAG 5527.'
                      : 'Read-only preview while outbound is disabled. NFFI-imported can be re-broadcast to OTHER NFFI peers (echo-prevention at runtime); CoT-imported is excluded (cross-protocol).'}
                  </div>
                  <div className="grid gap-3 grid-cols-1 lg:grid-cols-2">
                    {renderColumn('COP Datasets',  'fa-folder-open',     'bg-slate-100 text-slate-700',   groups.cop,  'No internal COP layers')}
                    {renderColumn('NFFI Datasets', 'fa-handshake-angle', 'bg-violet-50 text-violet-700',  groups.nffi, 'No NFFI-imported layers')}
                  </div>
                </div>

                <div className="inline-flex items-center gap-2 text-xs text-mist border-t border-line pt-3">
                  <i className="fa-sharp fa-solid fa-circle-info text-slate-400" />
                  Node ID: <span className="font-mono text-ink">{hot?.nodeId || '—'}</span> ·
                  Version: <span className="font-mono text-ink">NFFI {hot?.version || '—'}</span> ·
                  Endpoint: <code className="rounded bg-slate-100 px-1 text-[10px]">{endpointStr}</code>
                </div>
              </section>
            );
          })()}

          {/* ── Recent messages ── */}
          <section className="rounded-[28px] border border-line bg-white p-5 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-mist">
                <i className="fa-sharp fa-solid fa-clock-rotate-left text-slate-400" />
                Recent Messages
              </h3>
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex gap-1 rounded-xl border border-line p-1">
                  <button
                    className={`px-3 py-1 text-xs font-medium rounded-lg ${messageDir === 'rx' ? 'bg-ink text-white' : 'text-slate-600'}`}
                    onClick={() => setMessageDir('rx')}
                  >
                    <i className="fa-sharp fa-solid fa-download" /> Inbound (RX)
                  </button>
                  <button
                    className={`px-3 py-1 text-xs font-medium rounded-lg ${messageDir === 'tx' ? 'bg-ink text-white' : 'text-slate-600'}`}
                    onClick={() => setMessageDir('tx')}
                  >
                    <i className="fa-sharp fa-solid fa-upload" /> Outbound (TX)
                  </button>
                </div>
                <ExportButtons
                  getRows={() => searchedMessages}
                  columns={messageExportColumns}
                  filenameBase={`nffi-messages-${messageDir}`}
                  disabled={searchedMessages.length === 0}
                  small
                />
              </div>
            </div>

            <div className="max-w-md">
              <SearchInput
                value={msgSearch}
                onChange={setMsgSearch}
                placeholder="Search: peer / track id / category / callsign / via / operation…"
                small
              />
            </div>

            {messages.length === 0 ? (
              <div className="py-6 text-center text-xs text-mist">No {messageDir.toUpperCase()} messages yet.</div>
            ) : msgTable.pageRows.length === 0 ? (
              <div className="py-6 text-center text-xs text-mist">No messages match the search.</div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="border-b border-line text-[10px] font-semibold uppercase tracking-[0.12em] text-mist">
                        <th className="pb-2 pr-3 cursor-pointer select-none whitespace-nowrap" onClick={() => msgTable.toggleSort('ts')}>
                          Time<SortIcon active={msgTable.sortKey === 'ts'} dir={msgTable.sortDir} />
                        </th>
                        <th className="pb-2 pr-3 cursor-pointer select-none whitespace-nowrap" onClick={() => msgTable.toggleSort('peerId')}>
                          Peer<SortIcon active={msgTable.sortKey === 'peerId'} dir={msgTable.sortDir} />
                        </th>
                        {messageDir === 'rx' && <th className="pb-2 pr-3 cursor-pointer select-none whitespace-nowrap" onClick={() => msgTable.toggleSort('trackId')}>
                          Track ID<SortIcon active={msgTable.sortKey === 'trackId'} dir={msgTable.sortDir} />
                        </th>}
                        {messageDir === 'rx' && <th className="pb-2 pr-3 cursor-pointer select-none whitespace-nowrap" onClick={() => msgTable.toggleSort('typeId')}>
                          Type ID<SortIcon active={msgTable.sortKey === 'typeId'} dir={msgTable.sortDir} />
                        </th>}
                        {messageDir === 'rx' && <th className="pb-2 pr-3 cursor-pointer select-none whitespace-nowrap" onClick={() => msgTable.toggleSort('category')}>
                          Category<SortIcon active={msgTable.sortKey === 'category'} dir={msgTable.sortDir} />
                        </th>}
                        {messageDir === 'rx' && <th className="pb-2 pr-3 whitespace-nowrap">Lat,Lon</th>}
                        {messageDir === 'rx' && <th className="pb-2 pr-3 cursor-pointer select-none whitespace-nowrap" onClick={() => msgTable.toggleSort('callsign')}>
                          Callsign<SortIcon active={msgTable.sortKey === 'callsign'} dir={msgTable.sortDir} />
                        </th>}
                        {messageDir === 'rx' && <th className="pb-2 pr-3 whitespace-nowrap">Via</th>}
                        {messageDir === 'tx' && <th className="pb-2 pr-3 whitespace-nowrap">Operation</th>}
                        {messageDir === 'tx' && <th className="pb-2 pr-3 whitespace-nowrap">Remote</th>}
                        {messageDir === 'tx' && <th className="pb-2 pr-3 text-right whitespace-nowrap">Tracks</th>}
                        {messageDir === 'tx' && <th className="pb-2 pr-3 text-right whitespace-nowrap">Resp Bytes</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {msgTable.pageRows.map((m, idx) => (
                        <tr key={idx} className="border-b border-line last:border-0 hover:bg-slate-50">
                          <td className="py-2 pr-3 font-mono text-mist tabular-nums">{m.ts ? formatDate(m.ts, { second: '2-digit', fractionalSecondDigits: 0 }) : '—'}</td>
                          <td className="py-2 pr-3 font-mono text-ink">{m.peerId}</td>
                          {messageDir === 'rx' && <td className="py-2 pr-3 font-mono text-mist max-w-[200px] truncate" title={m.trackId}>{m.trackId || '—'}</td>}
                          {messageDir === 'rx' && <td className="py-2 pr-3 font-mono text-mist">{m.typeId || '—'}</td>}
                          {messageDir === 'rx' && <td className="py-2 pr-3 text-mist">{m.category || '—'}{m.subCategory ? ` / ${m.subCategory}` : ''}</td>}
                          {messageDir === 'rx' && <td className="py-2 pr-3 font-mono text-mist tabular-nums">{m.lat?.toFixed(4)},{m.lon?.toFixed(4)}</td>}
                          {messageDir === 'rx' && <td className="py-2 pr-3 text-ink">{m.callsign || '—'}</td>}
                          {messageDir === 'rx' && (
                            <td className="py-2 pr-3">
                              <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${
                                m.lookupVia === 'milsymbol' ? 'bg-emerald-50 text-emerald-700' :
                                m.lookupVia === 'nffi_category' ? 'bg-sky-50 text-sky-700' :
                                'bg-rose-50 text-rose-700'
                              }`}>
                                {m.lookupVia || 'unmapped'}
                              </span>
                            </td>
                          )}
                          {messageDir === 'tx' && <td className="py-2 pr-3 font-mono text-mist">{m.operation || '—'}</td>}
                          {messageDir === 'tx' && <td className="py-2 pr-3 font-mono text-mist">{m.remote || '—'}</td>}
                          {messageDir === 'tx' && <td className="py-2 pr-3 text-right tabular-nums font-mono text-mist">{m.trackCount ?? '—'}</td>}
                          {messageDir === 'tx' && <td className="py-2 pr-3 text-right tabular-nums font-mono text-mist">{bytesHuman(m.requestBytes ?? 0)}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  page={msgTable.page}
                  totalPages={msgTable.totalPages}
                  total={msgTable.total}
                  pageSize={MSG_PAGE_SIZE}
                  setPage={msgTable.setPage}
                />
              </>
            )}
          </section>

          {/* ── Unmapped categories ── */}
          {unmapped.length > 0 && (
            <section className="rounded-[28px] border border-line bg-white p-5 space-y-2">
              <h3 className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-mist">
                <i className="fa-sharp fa-solid fa-circle-exclamation text-amber-500" />
                Unmapped NFFI Categories
              </h3>
              <p className="text-xs text-mist">Inbound NFFI Category/SubCategory combinations that fell back to &quot;Unspecified&quot;. Each entry was logged with audit event <code className="rounded bg-slate-100 px-1 font-mono text-[10px]">nffi_unmapped_category</code>.</p>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-line text-[10px] font-semibold uppercase tracking-[0.12em] text-mist">
                      <th className="pb-2 pr-3">Category / SubCategory</th>
                      <th className="pb-2 pr-3 text-right">Count</th>
                      <th className="pb-2 pr-3">First seen</th>
                      <th className="pb-2 pr-3">Last seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unmapped.map((u) => (
                      <tr key={u.key} className="border-b border-line last:border-0 hover:bg-slate-50">
                        <td className="py-2 pr-3 font-mono text-ink">{u.key}</td>
                        <td className="py-2 pr-3 text-right tabular-nums font-mono text-mist">{u.count}</td>
                        <td className="py-2 pr-3 font-mono text-mist tabular-nums">{formatDate(u.firstSeen)}</td>
                        <td className="py-2 pr-3 font-mono text-mist tabular-nums">{formatDate(u.lastSeen)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>
      )}
    </PageShell>
  );
}
